import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, MapPin, DollarSign, Users, Edit, Send,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/api';
import { Role } from '@hirenfire/shared';
import type { ApiResponse, JobWithAgency } from '@hirenfire/shared';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [job, setJob] = useState<JobWithAgency | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const isAgency = user?.role === Role.AGENCY;
  const isOwner = isAgency && user?.id === job?.agencyId;

  useEffect(() => {
    async function fetchJob() {
      try {
        const res = await api.get<ApiResponse<JobWithAgency>>(`/jobs/${id}`);
        if (res.data.data) setJob(res.data.data);
      } catch {
        navigate('/jobs');
      } finally {
        setIsLoading(false);
      }
    }
    fetchJob();
  }, [id, navigate]);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      await api.post('/applications', { jobId: id, coverLetter });
      setShowApplyModal(false);
      setCoverLetter('');
    } catch {
      // handle error
    } finally {
      setIsApplying(false);
    }
  };

  if (isLoading || !job) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Jobs
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{job.title}</h1>
          <p className="text-gray-400 mt-1">
            Posted by{' '}
            <Link
              to={`/profile/${job.agencyId}`}
              className="text-primary-400 hover:text-primary-300"
            >
              {job.agency.displayName}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isOwner ? (
            <Link to={`/jobs/${job.id}`}>
              <Button variant="secondary" leftIcon={<Edit className="w-4 h-4" />}>
                Edit Job
              </Button>
            </Link>
          ) : user?.role === Role.WORKER ? (
            <Button
              leftIcon={<Send className="w-4 h-4" />}
              onClick={() => setShowApplyModal(true)}
            >
              Apply Now
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>Job Description</CardHeader>
            <CardBody>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 whitespace-pre-wrap">{job.description}</p>
              </div>
            </CardBody>
          </Card>

          {job.requirements.length > 0 && (
            <Card>
              <CardHeader>Requirements</CardHeader>
              <CardBody>
                <ul className="space-y-2">
                  {job.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 flex-shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardBody className="space-y-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-success-400" />
                <div>
                  <p className="text-xs text-gray-500">Budget</p>
                  <p className="font-semibold text-white">
                    ${job.budget} {job.currency}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-primary-400" />
                <div>
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="font-semibold text-white">{job.type}</p>
                </div>
              </div>

              {job.timezone && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-accent-400" />
                  <div>
                    <p className="text-xs text-gray-500">Timezone</p>
                    <p className="font-semibold text-white">{job.timezone}</p>
                  </div>
                </div>
              )}

              {job.applicationCount !== undefined && (
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-warning-400" />
                  <div>
                    <p className="text-xs text-gray-500">Applications</p>
                    <p className="font-semibold text-white">{job.applicationCount}</p>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-dark-600 flex flex-wrap gap-2">
                <Badge variant="primary">{job.type}</Badge>
                <Badge variant="purple">{job.category}</Badge>
                <Badge>{job.chain}</Badge>
                <Badge>{job.status}</Badge>
              </div>

              {job.shiftsPerWeek && (
                <div className="text-sm text-gray-400">
                  {job.shiftsPerWeek} shifts/week
                  {job.hoursPerShift && `, ${job.hoursPerShift}h each`}
                </div>
              )}

              <p className="text-xs text-gray-500">
                Posted {new Date(job.createdAt).toLocaleDateString()}
              </p>
            </CardBody>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        title="Apply for this Job"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">
            Applying to: <span className="text-white font-medium">{job.title}</span>
          </p>
          <Textarea
            label="Cover Letter"
            placeholder="Tell the agency why you're a great fit for this role..."
            value={coverLetter}
            onChange={(e) => setCoverLetter(e.target.value)}
            maxChars={2000}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowApplyModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleApply} isLoading={isApplying}>
              Submit Application
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
